/**
 * The Reports tab: PRODUCT.md §6's reports, one at a time, each a plain list with its total.
 *
 * Phone first, so a report is a list rather than a wide table: a name on the left, the amount
 * on the right, and what makes it up underneath. Filters sit above it and live in the URL
 * (`filters.ts`).
 */

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronRight, FileDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Field, Input, Notice, Select } from "@/components/ui/Field";
import { OrderStatusBadge } from "@/features/orders/OrderBits";
import { Amount } from "@/features/receipts/ReceiptBits";
import { businessesQuery } from "@/features/settings/queries";
import { cn } from "@/lib/cn";
import { todayIso } from "@/lib/dates";
import type { Money } from "@/lib/money";
import {
  GROUPINGS,
  PERIODS,
  REPORTS,
  periodRange,
  resolve,
  toSearch,
  type Filters,
  type Period,
  type Report,
} from "./filters";
import {
  expensesQuery,
  householdQuery,
  incomeQuery,
  ordersReportQuery,
  taxQuery,
  type ExpenseReport,
} from "./queries";

const chip = (selected: boolean) =>
  cn(
    "min-h-9 shrink-0 rounded-full border px-3 text-sm font-medium",
    selected
      ? "border-brand-700 bg-brand-700 text-white"
      : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50",
  );

export function ReportsPage() {
  const { t } = useTranslation();
  const search = useSearch({ from: "/app/reports" });
  const navigate = useNavigate({ from: "/reports" });
  const filters = resolve(search, todayIso());

  function change(next: Partial<Filters>) {
    void navigate({ search: toSearch({ ...filters, ...next }), replace: true });
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-stone-900">{t("reports.title")}</h1>
        <Link
          to="/reports/export"
          className="text-brand-700 inline-flex items-center gap-1.5 text-sm underline"
        >
          <FileDown className="h-4 w-4" aria-hidden />
          {t("reports.export.link")}
        </Link>
      </header>

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {REPORTS.map((report) => (
          <button
            key={report}
            type="button"
            aria-pressed={filters.report === report}
            className={chip(filters.report === report)}
            onClick={() => change({ report, by: GROUPINGS[report][0] ?? "" })}
          >
            {t(`reports.kinds.${report}`)}
          </button>
        ))}
      </div>

      <FilterBar filters={filters} onChange={change} />

      {GROUPINGS[filters.report].length > 0 && (
        <div className="space-y-1">
          <p className="text-sm text-stone-600">{t("reports.groupBy")}</p>
          <div className="flex flex-wrap gap-2">
            {GROUPINGS[filters.report].map((by: string) => (
              <button
                key={by}
                type="button"
                aria-pressed={filters.by === by}
                className={chip(filters.by === by)}
                onClick={() => change({ by })}
              >
                {t(`reports.by.${by}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      <ReportBody filters={filters} />
    </section>
  );
}

// --- filters ------------------------------------------------------------------------------------

function FilterBar({
  filters,
  onChange,
}: {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
}) {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);

  function choosePeriod(period: Period) {
    if (period !== "custom") {
      onChange({ period, start: null, end: null });
      return;
    }
    // A custom range starts from the dates on screen, so it is adjusted rather than typed; from
    // "all time", which has none, it starts at this month.
    const month = periodRange("this-month", todayIso());
    onChange({ period, start: filters.start ?? month.start, end: filters.end ?? month.end });
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        {filters.report !== "household" && (
          <div className="min-w-0 flex-1">
            <Field name="report-business" label={t("reports.business")}>
              <Select
                id="report-business"
                value={filters.business ?? ""}
                onChange={(event) => onChange({ business: event.target.value || null })}
              >
                <option value="">
                  {filters.report === "expenses"
                    ? t("reports.everyBucket")
                    : t("reports.everyBusiness")}
                </option>
                {(businesses.data ?? []).map((business) => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <Field name="report-period" label={t("reports.period")}>
            <Select
              id="report-period"
              value={filters.period}
              onChange={(event) => choosePeriod(event.target.value as Period)}
            >
              {PERIODS.map((period) => (
                <option key={period} value={period}>
                  {t(`reports.periods.${period}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>
      {filters.period === "custom" && (
        <div className="flex gap-3">
          <div className="min-w-0 flex-1">
            <Field name="report-start" label={t("reports.from")}>
              <Input
                id="report-start"
                type="date"
                value={filters.start ?? ""}
                onChange={(event) => onChange({ start: event.target.value || null })}
              />
            </Field>
          </div>
          <div className="min-w-0 flex-1">
            <Field name="report-end" label={t("reports.to")}>
              <Input
                id="report-end"
                type="date"
                value={filters.end ?? ""}
                onChange={(event) => onChange({ end: event.target.value || null })}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

// --- the reports --------------------------------------------------------------------------------

function ReportBody({ filters }: { filters: Filters }) {
  const bodies: Record<Report, ReactNode> = {
    expenses: <Expenses filters={filters} household={false} />,
    household: <Expenses filters={filters} household />,
    income: <Income filters={filters} />,
    orders: <Orders filters={filters} />,
    tax: <Tax filters={filters} />,
  };
  return bodies[filters.report];
}

/** Loading, a failure, or nothing in the period: what every report shows instead of rows. */
function State({
  query,
  empty,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown };
  empty: boolean;
}) {
  const { t } = useTranslation();
  if (query.isPending) return <p className="text-stone-500">{t("common.loading")}</p>;
  if (query.isError) {
    const reason = query.error instanceof Error ? query.error.message : "";
    return <Notice tone="error">{reason || t("reports.loadFailed")}</Notice>;
  }
  if (empty) return <Notice tone="info">{t("reports.empty")}</Notice>;
  return null;
}

function Undated({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return <Notice tone="info">{t("reports.undated", { count })}</Notice>;
}

function Expenses({ filters, household }: { filters: Filters; household: boolean }) {
  const { t } = useTranslation();
  const report = useQuery(household ? householdQuery(filters) : expensesQuery(filters));
  const rows = report.data?.rows ?? [];

  function title(row: ExpenseReport["rows"][number]): string {
    if (row.key === "household") return t("settings.categories.household");
    if (row.name) return row.name;
    return t(`reports.missing.${filters.by}`);
  }

  return (
    <div className="space-y-3">
      <State query={report} empty={report.isSuccess && rows.length === 0} />
      {rows.length > 0 && (
        <List>
          {rows.map((row) => (
            <Row
              key={`${row.key ?? ""}-${row.bucket?.business_id ?? ""}-${row.amount.currency}`}
              title={title(row)}
              detail={[
                row.bucket &&
                  !household &&
                  (row.bucket.business_name || t("settings.categories.household")),
                t("reports.lines", { count: row.lines }),
              ]}
              amount={row.amount}
            />
          ))}
        </List>
      )}
      {rows.length > 0 && <Totals rows={[[t("reports.total"), report.data?.totals ?? []]]} />}
      {report.data && <Undated count={report.data.undated} />}
      {rows.length > 0 && <p className="text-sm text-stone-500">{t("reports.beforeTax")}</p>}
    </div>
  );
}

function Income({ filters }: { filters: Filters }) {
  const { t } = useTranslation();
  const report = useQuery(incomeQuery(filters));
  const rows = report.data?.rows ?? [];

  return (
    <div className="space-y-3">
      <State query={report} empty={report.isSuccess && rows.length === 0} />
      {rows.length > 0 && (
        <List>
          {rows.map((row) => (
            <Row
              key={`${row.key}-${row.net.currency}`}
              title={filters.by === "status" ? t(`orders.status.${row.key}`) : row.name}
              detail={
                row.paid_out.minor > 0
                  ? [
                      <MoneyText key="in" label="reports.received" money={row.paid_in} />,
                      <MoneyText key="out" label="reports.givenBack" money={row.paid_out} />,
                    ]
                  : [t("reports.payments", { count: row.payments })]
              }
              amount={row.net}
            />
          ))}
        </List>
      )}
      {report.data && rows.length > 0 && (
        <Totals
          rows={[
            [t("reports.received"), report.data.paid_in],
            [t("reports.givenBack"), report.data.paid_out],
            [t("reports.net"), report.data.net],
          ]}
        />
      )}
    </div>
  );
}

function Orders({ filters }: { filters: Filters }) {
  const { t } = useTranslation();
  const report = useQuery(ordersReportQuery(filters));
  const rows = report.data?.rows ?? [];

  return (
    <div className="space-y-3">
      <State query={report} empty={report.isSuccess && rows.length === 0} />
      {rows.length > 0 && (
        <List>
          {rows.map((row) => (
            <li key={row.id}>
              <Link
                to="/orders/$orderId"
                params={{ orderId: row.id }}
                className="flex items-start gap-3 px-4 py-3 hover:bg-stone-50"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="truncate text-stone-800">{row.description}</p>
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-stone-500">
                    <span className="truncate">{row.customer_name}</span>
                    <OrderStatusBadge status={row.status} />
                  </p>
                  <p className="text-sm text-stone-500">
                    <MoneyText label="reports.paid" money={row.net_paid} />
                    {" · "}
                    <MoneyText label="reports.cost" money={row.cost} />
                  </p>
                </div>
                <div className="text-right">
                  <Amount
                    money={row.margin}
                    className={row.margin.minor < 0 ? "text-red-700" : "text-stone-800"}
                  />
                  <p className="text-xs text-stone-500">{t("reports.margin")}</p>
                </div>
                <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-stone-300" aria-hidden />
              </Link>
            </li>
          ))}
        </List>
      )}
      {report.data && rows.length > 0 && (
        <Totals
          rows={[
            [t("reports.paid"), [report.data.net_paid]],
            [t("reports.cost"), [report.data.cost]],
            [t("reports.margin"), [report.data.margin]],
          ]}
        />
      )}
      {rows.length > 0 && <p className="text-sm text-stone-500">{t("reports.ordersNote")}</p>}
    </div>
  );
}

function Tax({ filters }: { filters: Filters }) {
  const { t } = useTranslation();
  const report = useQuery(taxQuery(filters));
  const rows = report.data?.rows ?? [];
  const shared = rows.some((row) => row.shared.minor > 0);

  return (
    <div className="space-y-3">
      <State query={report} empty={report.isSuccess && rows.length === 0} />
      {rows.length > 0 && (
        <List>
          {rows.map((row) => (
            <Row
              key={`${row.label}-${row.whole.currency}`}
              title={row.label}
              detail={[
                t("reports.receipts", { count: row.receipts }),
                row.shared.minor > 0 && (
                  <MoneyText key="shared" label="reports.sharedPart" money={row.shared} />
                ),
              ]}
              amount={row.whole}
            />
          ))}
        </List>
      )}
      {report.data && rows.length > 0 && (
        <Totals
          rows={[
            [t("reports.total"), report.data.whole],
            ...(shared
              ? ([[t("reports.sharedTotal"), report.data.shared]] as [string, Money[]][])
              : []),
          ]}
        />
      )}
      {shared && <Notice tone="info">{t("reports.sharedExplained")}</Notice>}
      {report.data && <Undated count={report.data.undated} />}
    </div>
  );
}

// --- pieces -------------------------------------------------------------------------------------

function List({ children }: { children: ReactNode }) {
  return (
    <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
      {children}
    </ul>
  );
}

function Row({ title, detail, amount }: { title: string; detail: ReactNode[]; amount: Money }) {
  const shown = detail.filter(Boolean);
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-stone-800">{title}</p>
        {shown.length > 0 && (
          <p className="text-sm text-stone-500">
            {shown.map((part, index) => (
              <span key={index}>
                {index > 0 && " · "}
                {part}
              </span>
            ))}
          </p>
        )}
      </div>
      <Amount
        money={amount}
        className={amount.minor < 0 ? "text-red-700" : "font-medium text-stone-800"}
      />
    </li>
  );
}

/** "Received $450.00": a label with an amount formatted the one way amounts are. */
function MoneyText({ label, money }: { label: string; money: Money }) {
  const { t } = useTranslation();
  return (
    <>
      {t(label)} <Amount money={money} />
    </>
  );
}

/** Each line a label and its totals, one per currency. */
function Totals({ rows }: { rows: [string, Money[]][] }) {
  return (
    <dl className="space-y-1.5 rounded-lg border border-stone-200 bg-white px-4 py-3">
      {rows.map(([label, amounts], index) => (
        <div key={label} className="flex justify-between gap-3">
          <dt
            className={
              index === rows.length - 1 ? "font-semibold text-stone-900" : "text-stone-600"
            }
          >
            {label}
          </dt>
          <dd className="text-right">
            {amounts.map((amount) => (
              <Amount
                key={amount.currency}
                money={amount}
                className={cn("block", index === rows.length - 1 && "font-semibold text-stone-900")}
              />
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
}
